import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { BinColor } from '@cwy/config/contracts';
import { binRamp } from '@/lib/bins';

/** Shared colour blocks keep the overview and curriculum workspace in the same language. */
export function AdminBlock({
  label,
  value,
  detail,
  tone,
  to,
}: {
  label: string;
  value: ReactNode;
  detail: string;
  tone: BinColor;
  to?: string;
}) {
  const ramp = binRamp(tone);
  const content = (
    <>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold">{label}</span>
        <span aria-hidden="true" className="text-lg">
          {to ? '↗' : '●'}
        </span>
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight">{value}</div>
      <p className="mt-2 text-xs leading-relaxed">{detail}</p>
    </>
  );
  const className =
    'block min-w-0 rounded-card border p-4 no-underline transition-colors duration-150';
  const style = { background: ramp.bg, borderColor: 'var(--border-default)', color: ramp.text };

  return to ? (
    <Link
      to={to}
      className={`${className} hover:!border-current focus-visible:outline-2 focus-visible:outline-offset-4`}
      style={style}
    >
      {content}
    </Link>
  ) : (
    <div className={className} style={style}>
      {content}
    </div>
  );
}

export function AdminHero({
  eyebrow,
  title,
  description,
  action,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="overflow-hidden rounded-card border border-line bg-card">
      <div className="grid gap-6 p-5 sm:p-7 lg:grid-cols-[1.4fr_1fr] lg:items-center">
        <div>
          <p className="mb-3 flex items-center gap-2 text-fs-11 font-bold uppercase tracking-[0.16em] text-forest">
            <span className="size-2 rounded-full bg-bin-green" aria-hidden="true" />
            {eyebrow}
          </p>
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-forest sm:text-4xl">
            {title}
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-ink-soft">{description}</p>
          {action && <div className="mt-5 flex flex-wrap gap-2">{action}</div>}
        </div>
        {children}
      </div>
      <div className="grid h-1.5 grid-cols-5" aria-hidden="true">
        <span className="bg-bin-green-fill" />
        <span className="bg-bin-purple-fill" />
        <span className="bg-bin-blue-fill" />
        <span className="bg-bin-orange-fill" />
        <span className="bg-bin-pink-fill" />
      </div>
    </header>
  );
}
